import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getGetProductStatsQueryKey,
  getGetProductsQueryKey,
  useDeleteProduct,
  useGetProductCategories,
} from "@/api";
import { useProductsList } from "@/hooks/useProductsList";
import { formatPersianNumber } from "@/lib/format/persian";
import { useSessionStore } from "@/stores/session.store";

const PER_PAGE = 10;

export function useProductsPage() {
  const { t } = useTranslation(["products", "common"]);
  const queryClient = useQueryClient();
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<number | "all">("all");
  const productsList = useProductsList();

  const { data: categoriesData } = useGetProductCategories(
    activeStoreId !== null ? { store_id: activeStoreId, per_page: 100 } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const categories = useMemo(
    () => categoriesData?.data?.data ?? [],
    [categoriesData],
  );

  const categoryNames = useMemo(
    () =>
      Object.fromEntries(categories.map((category) => [category.id, category.name] as const)),
    [categories],
  );

  const displayProducts = useMemo(() => {
    if (activeCategoryId === "all") return productsList.products;
    return productsList.products.filter((product) => product.category_id === activeCategoryId);
  }, [activeCategoryId, productsList.products]);

  const { mutate: deleteProduct, isPending: isDeleting } = useDeleteProduct({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() });
        if (activeStoreId !== null) {
          void queryClient.invalidateQueries({
            queryKey: getGetProductStatsQueryKey({ store_id: activeStoreId }),
          });
        }
        setProductToDelete(null);
      },
    },
  });

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      productsList.setSearch(event.target.value);
    },
    [productsList],
  );

  const handleDeleteRequest = useCallback((productId: number) => {
    setProductToDelete(productId);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (productToDelete === null) return;
    deleteProduct({ id: productToDelete });
  }, [deleteProduct, productToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setProductToDelete(null);
  }, []);

  const paginationLabel = useMemo(() => {
    if (!productsList.meta) return "";
    const from = (productsList.page - 1) * PER_PAGE + 1;
    const to = Math.min(productsList.page * PER_PAGE, productsList.meta.total);
    return t("products:pagination.showing", {
      from: formatPersianNumber(from),
      to: formatPersianNumber(to),
      total: formatPersianNumber(productsList.meta.total),
    });
  }, [productsList.meta, productsList.page, t]);

  const countLabel = useMemo(() => {
    const total = productsList.stats?.total_count ?? productsList.meta?.total ?? 0;
    if (total === 0) return t("products:countLabel.empty");
    return t("products:countLabel.total", { count: formatPersianNumber(total) });
  }, [productsList.meta?.total, productsList.stats?.total_count, t]);

  return {
    ...productsList,
    categories,
    categoryNames,
    activeCategoryId,
    setActiveCategoryId,
    displayProducts,
    productToDelete,
    isDeleting,
    handleSearchChange,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    paginationLabel,
    countLabel,
  };
}
